/**
 * Scratch & win popup: scratch first, email to claim.
 *
 * The prize is drawn just before the popup opens, so it is already under the
 * foil when the shopper starts scratching; it comes back as a signed ticket,
 * and nothing is created in Shopify yet. Once the shopper touches the tin the
 * pull is saved, so reloading the page shows the same prize instead of a new
 * draw. The customer and discount code are created when the shopper submits
 * an email, for the percent on the ticket.
 */
import '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';

const PLAYED_KEY = 'vyrl_scratch_played';
const DISMISSED_KEY = 'vyrl_scratch_dismissed';
const PULL_KEY = 'vyrl_scratch_pull';
const DISMISS_DAYS = 7;
const REVEAL_AT = 0.5;
const BRUSH_RADIUS = 26;
const MARKER_FONT_URL = 'https://fonts.googleapis.com/css2?family=Permanent+Marker&display=swap';

const MESSAGES = {
  invalid_email: 'Enter a valid email address.',
  consent_required: 'Tick the box to confirm you’re 18+ and agree to emails.',
  rate_limited: 'Too many tries — give it a few minutes.',
  paused: 'Scratch & win is taking a break. Check back soon.',
  expired: 'That pull expired. Scratch the tin again.',
  default: 'Something went wrong on our side. Try again in a moment.',
};

/** Storage can throw (private browsing, strict privacy settings). Never let it break the page. */
const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Without storage the popup may reappear on a later visit; acceptable.
    }
  },
  remove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Nothing stored to remove.
    }
  },
};

/** Expiry (ms) from the ticket's payload; the server still has the final say. */
function ticketExpiresAt(ticket) {
  const payload = ticket.split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
  return Number(JSON.parse(atob(payload)).x) || 0;
}

/**
 * The pull saved when the shopper first touched the tin, if its ticket is
 * still valid: { ticket, percent, revealed }.
 */
function loadPull() {
  try {
    const pull = JSON.parse(storage.get(PULL_KEY));
    if (typeof pull?.ticket !== 'string' || !Number.isInteger(pull.percent)) return null;
    if (ticketExpiresAt(pull.ticket) > Date.now()) return pull;
  } catch {
    // Unreadable: fall through and forget it.
  }
  storage.remove(PULL_KEY);
  return null;
}

function savePull(pull) {
  storage.set(PULL_KEY, JSON.stringify(pull));
}

function recentlyDismissed() {
  const at = Number(storage.get(DISMISSED_KEY));
  return Number.isFinite(at) && at > 0 && Date.now() - at < DISMISS_DAYS * 86_400_000;
}

function formatExpiry(iso) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(iso));
}

class ScratchWin extends HTMLElement {
  connectedCallback() {
    if (storage.get(PLAYED_KEY) || recentlyDismissed()) return;

    this.dialogComponent = this.querySelector('dialog-component');
    this.dialogEl = this.dialogComponent.querySelector('.sw__dialog');
    this.tin = this.querySelector('[data-tin]');
    this.canvas = this.querySelector('[data-foil]');
    this.prizeEl = this.querySelector('[data-prize]');
    this.tinMessage = this.querySelector('[data-tin-message]');
    this.revealBtn = this.querySelector('[data-reveal]');
    this.form = this.querySelector('[data-claim-form]');
    this.live = this.querySelector('[data-live]');
    this.round = 0;
    this.scratched = false;
    this.claimed = false;

    // The dialog's accessible name follows whichever step is visible: the
    // intro heading carries its id from Liquid, and the claim/done headings
    // are hidden in the intro step, so they get ids derived from it here.
    this.introHeading = this.querySelector('[data-step="intro"] h2');
    const introId = this.introHeading.id;
    const claimHeading = this.querySelector('[data-claim-heading]');
    const doneHeading = this.querySelector('[data-done-heading]');
    claimHeading.id = `${introId}-claim`;
    doneHeading.id = `${introId}-done`;
    this.headingIds = { intro: introId, claim: claimHeading.id, done: doneHeading.id };

    this.form.addEventListener('submit', (event) => this.onClaim(event));
    this.querySelector('[data-close]').addEventListener('click', () => this.dialogComponent.closeDialog());
    this.revealBtn.addEventListener('click', () => this.reveal());
    this.querySelector('[data-copy]').addEventListener('click', () => this.copyCode());
    this.dialogComponent.addEventListener('dialog:close', () => {
      // Someone who scratched but did not claim is shown it again later; only
      // an untouched popup counts as a dismissal.
      if (!this.scratched && !this.claimed) storage.set(DISMISSED_KEY, String(Date.now()));
    });

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.timer = window.setTimeout(() => this.start(), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
  }

  /**
   * Resume a saved pull, or draw before opening so the prize is already under
   * the foil when scratching starts (the draw is a round trip through the App
   * Proxy, which showed as a delay when it waited for the first touch).
   */
  async start() {
    this.pull = loadPull();
    if (!this.pull) {
      try {
        this.pull = await this.fetchPull();
      } catch (error) {
        // Paused or capped: don't show a tin nobody can play.
        if (error.message === 'paused') return;
        // Anything else: open anyway; the first touch retries the draw.
      }
    }
    if (this.isConnected) this.open();
  }

  open() {
    if (!document.querySelector(`link[href="${MARKER_FONT_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = MARKER_FONT_URL;
      document.head.append(link);
    }
    this.dialogComponent.showDialog();
    // Paint once the dialog is laid out, so the canvas gets its real size.
    requestAnimationFrame(() => requestAnimationFrame(() => this.prepareTin()));
  }

  show(step) {
    for (const panel of this.querySelectorAll('[data-step]')) panel.hidden = panel.dataset.step !== step;
    this.dialogEl.setAttribute('aria-labelledby', this.headingIds[step]);
  }

  /** One retry on 409 in_progress: the other request for this email is about to finish. */
  async post(payload, retried = false) {
    const response = await fetch(this.dataset.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    let body = {};
    try {
      body = await response.json();
    } catch {
      // Non-JSON (a proxy error page): handled as a server error below.
    }
    if (response.status === 409 && body.error === 'in_progress' && !retried) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      return this.post(payload, true);
    }
    if (!response.ok) throw new Error(body.error ?? 'default');
    return body;
  }

  setPercent(percent) {
    for (const el of this.querySelectorAll('[data-pct]')) el.textContent = `${percent}%`;
    const inner = document.createElement('div');
    inner.className = 'sw__prize-inner';
    const label = document.createElement('div');
    label.className = 'sw__prize-label';
    label.textContent = 'You pulled';
    const pct = document.createElement('div');
    pct.className = 'sw__prize-pct';
    pct.textContent = `${percent}%`;
    const off = document.createElement('div');
    off.className = 'sw__prize-off';
    off.textContent = 'Off';
    inner.append(label, pct, off);
    this.prizeEl.replaceChildren(inner);
  }

  async fetchPull() {
    const { ticket, percent } = await this.post({ action: 'draw' });
    if (typeof ticket !== 'string' || !Number.isInteger(percent)) throw new Error('default');
    return { ticket, percent, revealed: false };
  }

  /** The current pull; draws only if the pre-open draw failed, once per round. */
  draw() {
    if (this.pull) return Promise.resolve(this.pull);
    if (!this.drawing) {
      const mine = this.round;
      this.drawing = this.fetchPull()
        .then((pull) => {
          if (mine !== this.round) throw new Error('stale');
          this.pull = pull;
          this.setPercent(pull.percent);
          return pull;
        })
        .catch((error) => {
          if (error.message !== 'stale') this.onDrawFailed(error.message);
          throw error;
        });
    }
    return this.drawing;
  }

  onDrawFailed(code) {
    this.tinMessage.textContent = MESSAGES[code] ?? MESSAGES.default;
    this.tinMessage.hidden = false;
    if (code === 'paused') {
      this.tin.classList.add('is-disabled');
      this.canvas.style.pointerEvents = 'none';
      this.revealBtn.hidden = true;
      // Nothing was drawn; don't let a dead popup keep reappearing on every
      // page view while the store is paused/capped.
      this.scratched = false;
      storage.set(DISMISSED_KEY, String(Date.now()));
    } else {
      this.prepareTin({ keepMessage: true });
    }
  }

  prepareTin({ keepMessage = false } = {}) {
    this.round += 1;
    const mine = this.round;
    this.revealed = false;
    this.drawing = null;
    this.strokes = 0;
    this.prizeEl.replaceChildren();
    if (this.pull) this.setPercent(this.pull.percent);
    this.tin.classList.remove('is-active', 'is-revealed', 'is-disabled');
    this.revealBtn.hidden = false;
    if (!keepMessage) this.tinMessage.hidden = true;

    // A fresh canvas drops the previous round's listeners and scratch marks.
    const fresh = this.canvas.cloneNode();
    fresh.className = 'sw__foil';
    fresh.removeAttribute('style');
    this.canvas.replaceWith(fresh);
    this.canvas = fresh;

    const image = new Image();
    // The tin is served from the Shopify CDN (Access-Control-Allow-Origin: *);
    // without this the canvas is tainted and clearedFraction() always returns
    // 1 via its catch branch, revealing after a handful of strokes.
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (mine !== this.round) return;
      this.paint(image);
      // Under prefers-reduced-motion the scratch layer is not drawn; only the
      // Reveal button works (spec §6). The canvas is fresh here (replaceWith
      // above), so this reset is applied every round.
      if (prefersReducedMotion()) {
        this.canvas.style.pointerEvents = 'none';
      } else {
        this.bind();
      }
      // Revealed before a reload: go straight back to the claim step.
      if (this.pull?.revealed) this.reveal();
    };
    image.src = this.dataset.tin;
  }

  paint(image) {
    const rect = this.tin.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    this.canvas.width = Math.round(rect.width * ratio);
    this.canvas.height = Math.round(rect.height * ratio);
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.save();
    ctx.beginPath();
    ctx.arc(rect.width / 2, rect.height / 2, rect.width / 2, 0, Math.PI * 2);
    ctx.clip();
    const [sx, sy, size] = (this.dataset.tinCrop || '0,0,1').split(',').map(Number);
    const w = image.naturalWidth;
    ctx.drawImage(image, sx * w, sy * w, size * w, size * w, 0, 0, rect.width, rect.height);
    ctx.restore();
    this.ctx = ctx;
  }

  bind() {
    const canvas = this.canvas;
    let down = false;
    const point = (event) => {
      const r = canvas.getBoundingClientRect();
      return [event.clientX - r.left, event.clientY - r.top];
    };
    const erase = ([x, y]) => {
      this.ctx.globalCompositeOperation = 'destination-out';
      this.ctx.beginPath();
      this.ctx.arc(x, y, BRUSH_RADIUS, 0, Math.PI * 2);
      this.ctx.fill();
    };

    canvas.addEventListener('pointerdown', (event) => {
      if (this.revealed) return;
      down = true;
      this.scratched = true;
      this.tin.classList.add('is-active');
      canvas.setPointerCapture(event.pointerId);
      // Touching the tin locks the pull in: a reload shows the same prize.
      this.draw()
        .then((pull) => savePull(pull))
        .catch(() => {});
      erase(point(event));
    });
    canvas.addEventListener('pointermove', (event) => {
      if (!down || this.revealed) return;
      erase(point(event));
      this.strokes += 1;
      if (this.strokes % 6 === 0 && this.clearedFraction() >= REVEAL_AT) this.reveal();
    });
    const stop = () => {
      down = false;
    };
    canvas.addEventListener('pointerup', stop);
    canvas.addEventListener('pointercancel', stop);
  }

  /** Share of the lid scratched away; the transparent corners (~21.5% of the square) are excluded. */
  clearedFraction() {
    try {
      const { data } = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
      let cleared = 0;
      let sampled = 0;
      for (let i = 3; i < data.length; i += 64) {
        sampled += 1;
        if (data[i] === 0) cleared += 1;
      }
      return Math.max(0, (cleared / sampled - 0.215) / 0.785);
    } catch {
      // A custom tin image served without CORS headers taints the canvas; treat as cleared.
      return 1;
    }
  }

  reveal() {
    if (this.revealed) return;
    this.revealed = true;
    this.scratched = true;
    const mine = this.round;
    this.draw()
      .then((pull) => {
        if (mine !== this.round) return;
        this.pull = { ...pull, revealed: true };
        savePull(this.pull);
        this.canvas.classList.add('is-cleared');
        this.tin.classList.add('is-revealed');
        this.revealBtn.hidden = true;
        this.live.textContent = `You pulled ${pull.percent} percent off your first order.`;
        window.setTimeout(() => {
          if (mine !== this.round) return;
          this.show('claim');
          this.querySelector('[data-claim-heading]').focus({ preventScroll: true });
        }, prefersReducedMotion() ? 0 : 450);
      })
      .catch(() => {
        if (mine === this.round) this.revealed = false;
      });
  }

  formError(message, focusEl) {
    const el = this.querySelector('[data-form-error]');
    el.textContent = message;
    el.hidden = false;
    this.announce(message);
    focusEl?.focus();
  }

  /**
   * Writes to the live region so screen readers announce errors that don't
   * move focus. Cleared first so a repeated identical message is still
   * announced as a fresh change.
   */
  announce(message) {
    this.live.textContent = '';
    requestAnimationFrame(() => {
      this.live.textContent = message;
    });
  }

  async onClaim(event) {
    event.preventDefault();
    if (this.submitting) return;
    const email = this.form.elements.email;
    const consent = this.form.elements.consent;
    if (!email.value.trim() || !email.checkValidity()) return this.formError(MESSAGES.invalid_email, email);
    if (!consent.checked) return this.formError(MESSAGES.consent_required);
    this.querySelector('[data-form-error]').hidden = true;

    this.submitting = true;
    const button = this.form.querySelector('[type="submit"]');
    button.setAttribute('aria-disabled', 'true');
    try {
      const result = await this.post({ action: 'claim', email: email.value, consent: true, ticket: this.pull.ticket });
      this.claimed = true;
      storage.set(PLAYED_KEY, JSON.stringify(result));
      storage.remove(PULL_KEY);
      this.showDone(result);
    } catch (error) {
      const code = error.message;
      if (code === 'ticket_expired' || code === 'invalid_ticket' || code === 'ticket_used') {
        this.show('intro');
        this.tinMessage.textContent = MESSAGES.expired;
        this.tinMessage.hidden = false;
        this.announce(MESSAGES.expired);
        this.introHeading.focus({ preventScroll: true });
        // The saved pull is spent or dead; the next touch draws a new one.
        this.pull = null;
        storage.remove(PULL_KEY);
        this.prepareTin({ keepMessage: true });
      } else {
        this.formError(MESSAGES[code] ?? MESSAGES.default, code === 'invalid_email' ? email : undefined);
      }
    } finally {
      this.submitting = false;
      button.removeAttribute('aria-disabled');
    }
  }

  showDone({ code, percent, expiresAt, returning }) {
    // A returning player's code may be a different percent from what they
    // just scratched; show what the code is actually worth.
    this.setPercent(percent);
    this.querySelector('[data-done-kicker]').textContent = returning ? 'Welcome back' : 'It’s yours';
    this.querySelector('[data-done-heading]').textContent = returning
      ? `You already have ${percent}% off.`
      : `${percent}% off. Go get it.`;
    this.querySelector('[data-code]').textContent = code;
    const apply = this.querySelector('[data-apply]');
    apply.href = `/discount/${encodeURIComponent(code)}?redirect=/collections/all`;
    apply.textContent = `Shop with ${percent}% off`;
    this.querySelector('[data-expiry]').textContent = `Use it by ${formatExpiry(expiresAt)}. It’s applied at checkout when you shop from here.`;
    this.show('done');
    this.querySelector('[data-done-heading]').focus({ preventScroll: true });
    this.live.textContent = `Your code is ${code}.`;
  }

  async copyCode() {
    const button = this.querySelector('[data-copy]');
    const code = this.querySelector('[data-code]').textContent;
    try {
      await navigator.clipboard.writeText(code);
      button.textContent = 'Copied';
    } catch {
      const range = document.createRange();
      range.selectNodeContents(this.querySelector('[data-code]'));
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      button.textContent = 'Selected';
    }
  }
}

if (!customElements.get('scratch-win')) customElements.define('scratch-win', ScratchWin);
